import React from 'react'

import { connect } from 'react-redux'
import { getSelectedStore } from 'x/redux/selectors'
import _ from 'lodash'

import { FlatList, ListRenderItemInfo, RefreshControl, TouchableOpacity } from 'react-native'

import * as util from 'x/utils/util'
import { inventoryQtyAdjustHistoryList } from 'x/utils/api/inventory'
import * as NavActions from 'x/utils/navigation'
import { COLORS } from 'x/config/styles'
import time from 'x/utils/time'
import {
  DEFAULT_QTY_ADJUSTMENT_HISTORY_FILTER,
  convertFilterToRequestBody,
  mergeWithDefaultFilter,
  isFilterDifferentFromDefault,
} from 'x/utils/inventoryFilterUtils'

import VStack from 'xui/components/VStack'
import HStack from 'xui/components/HStack'
import XText from 'xui/components/XText'
import XContainer from 'xui/components/XContainer'
import XCustomHeader from 'xui/components/XCustomHeader'
import XInput from 'xui/components/XInput'
import XSpinner from 'xui/components/XSpinner'
import XIcon from 'xui/components/XIcon'
import FilterButton from 'xui/components/FilterButton'

import {
  IQtyAdjustmentHistoryListViewProps,
  IQtyAdjustmentHistoryListViewState,
  IQtyAdjustmentHistoryListItem,
  IQtyAdjustmentHistoryFilterState,
} from 'x/types'

const FETCH_LIMIT = 20
const SEARCH_DEBOUNCE_MS = 1200

class QtyAdjustmentHistoryListView extends React.Component<IQtyAdjustmentHistoryListViewProps, IQtyAdjustmentHistoryListViewState> {
  static displayName = 'QtyAdjustmentHistoryListView'

  isFetching?: boolean

  searchTimeoutId?: any

  constructor(props: IQtyAdjustmentHistoryListViewProps) {
    super(props)

    // Get nav params
    const pp_ids = util.getNavParam(props, 'pp_ids')
    const navDefaultFilter = util.getNavParam(props, 'defaultFilter')

    // Merge nav default filter with system default filter
    const defaultFilter = mergeWithDefaultFilter(navDefaultFilter, DEFAULT_QTY_ADJUSTMENT_HISTORY_FILTER)

    this.state = {
      isInitialized: false,
      isRefreshing: false,
      isFetchingMore: false,
      isReachEnd: false,

      historyItems: [],
      totalCount: 0,

      pp_ids: pp_ids || undefined,
      defaultFilter,
      currentFilter: { ...defaultFilter },
    }
  }

  async componentDidMount() {
    await util.setStatePromise(this, { isInitialized: true })
    await this._onRefresh()
  }

  componentWillUnmount() {
    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId)
    }
  }

  _getStoreId = (): number => {
    const { selectedStore } = this.props
    const navStoreId = util.getNavParam(this.props, 'store_id')
    return navStoreId || selectedStore.get('id')
  }

  _doFetch = async (offset = 0, limit = FETCH_LIMIT) => {
    const { historyItems, currentFilter, pp_ids } = this.state
    const store_id = this._getStoreId()

    // Use helper function to convert filter to request body
    const reqBody = convertFilterToRequestBody(currentFilter, store_id, pp_ids, offset, limit)

    try {
      const res = await inventoryQtyAdjustHistoryList(reqBody)
      // console.log('_doFetch res => ', res)

      const newState: Partial<IQtyAdjustmentHistoryListViewState> = {}

      if (res && res.histories) {
        if (offset === 0) {
          newState.historyItems = res.histories
        } else {
          newState.historyItems = historyItems.concat(res.histories)
        }

        if (res.total_count !== undefined) {
          newState.totalCount = res.total_count
        }

        // Check if reach end
        if (!res.has_next_page || res.histories.length < limit) {
          newState.isReachEnd = true
        }
      }

      if (!_.isEmpty(newState)) {
        await util.setStatePromise(this, newState)
      }
    } catch (err) {
      console.log('_doFetch err => ', err)
    }
  }

  _onRefresh = async () => {
    await this._loadInit()
  }

  _loadInit = async () => {
    if (this.isFetching) {
      return
    }
    this.isFetching = true
    await util.setStatePromise(this, { isRefreshing: true, isReachEnd: false })
    await util.delay(300) // slight delay to show refreshing indicator
    await this._doFetch(0)
    await util.setStatePromise(this, { isRefreshing: false })
    this.isFetching = false
  }

  _loadMore = async () => {
    const { historyItems, isReachEnd, isFetchingMore } = this.state

    if (this.isFetching || isReachEnd || isFetchingMore) {
      return
    }

    this.isFetching = true
    await util.setStatePromise(this, { isFetchingMore: true })

    const offset = historyItems.length
    await this._doFetch(offset)

    await util.setStatePromise(this, { isFetchingMore: false })
    this.isFetching = false
  }

  _onEndReached = () => {
    this._loadMore()
  }

  _onSearchChangeText = (text: string) => {
    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId)
    }

    // Update searchText in currentFilter
    this.setState(
      (prevState) => ({
        currentFilter: {
          ...prevState.currentFilter,
          searchText: text,
        },
      }),
      () => {
        this.searchTimeoutId = setTimeout(() => {
          this._onRefresh()
        }, SEARCH_DEBOUNCE_MS)
      }
    )
  }

  _onClearSearch = () => {
    this.setState(
      (prevState) => ({
        currentFilter: {
          ...prevState.currentFilter,
          searchText: '',
        },
      }),
      () => {
        this._onRefresh()
      }
    )
  }

  _goBack = () => {
    util.navGoBack(this.props)
  }

  _isFiltered = (): boolean => {
    const { currentFilter, defaultFilter } = this.state
    return isFilterDifferentFromDefault(currentFilter, defaultFilter)
  }

  _onApplyFilter = async (filter: IQtyAdjustmentHistoryFilterState) => {
    await util.setStatePromise(this, { currentFilter: filter })
    await this._onRefresh()
  }

  _navToFilterView = () => {
    const { navigation } = this.props
    const { currentFilter, defaultFilter } = this.state

    navigation.dispatch(
      NavActions.navToQtyAdjustmentHistoryListFilterView({
        defaultFilter,
        currentFilter,
        onApplyFilter: this._onApplyFilter,
      })
    )
  }

  _navToQtyAdjustmentDetailView = (item: IQtyAdjustmentHistoryListItem) => {
    const { navigation } = this.props
    const store_id = this._getStoreId()
    navigation.dispatch(
      NavActions.navToQtyAdjustmentDetailView({
        store_id,
        qty_adjustment_history_id: item.id,
      })
    )
  }

  _getModeText = (mode: 'a' | 'd' | 's' | 'c'): string => {
    switch (mode) {
      case 'a':
        return 'เพิ่ม'
      case 'd':
        return 'ลด'
      case 's':
        return 'ปรับ'
      case 'c':
        return 'นับ'
      default:
        return mode
    }
  }

  _getModeColor = (mode: 'a' | 'd' | 's' | 'c'): string => {
    switch (mode) {
      case 'a':
        return COLORS.BRAND_Success
      case 'd':
        return COLORS.BRAND_Danger
      case 's':
        return '#9333ea'
      case 'c':
        return COLORS.BRAND_Info
      default:
        return COLORS.TEXT_INACTIVE
    }
  }

  _renderFilterButton = () => {
    const isFiltered = this._isFiltered()
    return <FilterButton isFiltered={isFiltered} onPress={this._navToFilterView} />
  }

  _renderCustomHeader = () => {
    const isGoBackButtonHidden = util.getNavParam(this.props, 'isGoBackButtonHidden', false)

    return (
      <XCustomHeader
        title='ประวัติการปรับจำนวนสินค้า'
        headerLeftProps={isGoBackButtonHidden ? undefined : { backIcon: true, onPressItem: this._goBack }}
        renderHeaderRight={this._renderFilterButton}
      />
    )
  }

  _renderSearchBar = () => {
    const { currentFilter } = this.state
    const searchText = currentFilter.searchText || ''

    return (
      <HStack w='full' p='2' space='2' bg='white' borderBottomWidth='1' borderBottomColor='muted.200'>
        {/* <HStack flex={1} alignItems='center' bg='muted.100' borderRadius='lg' px='2'> */}
        <XInput
          flex={1}
          placeholder='ค้นหาด้วย ชื่อสินค้า, รหัสอ้างอิง, หมายเหตุ...'
          value={searchText}
          onChangeText={this._onSearchChangeText}
          InputLeftElement={
            <HStack w='34px' alignItems='center' justifyContent='center'>
              <XIcon name='search' family='FontAwesome' size='sm' color='muted.400' />
            </HStack>
          }
          InputRightElement={
            searchText.length > 0 ? (
              <TouchableOpacity onPress={this._onClearSearch}>
                <XIcon name='close-circle' family='Ionicons' size='sm' color='muted.400' />
              </TouchableOpacity>
            ) : null
          }
        />
        {/* </HStack> */}
      </HStack>
    )
  }

  _renderListItem = (info: ListRenderItemInfo<IQtyAdjustmentHistoryListItem>) => {
    const { item, index } = info
    const { mode, ref_code, note, created_at, id } = item

    const isOdd = index % 2 !== 0
    const modeText = this._getModeText(mode)
    const modeColor = this._getModeColor(mode)
    const createdAtText = created_at ? time.convertServerDateTimeToReadableDateTimeText(created_at) : '-'

    return (
      <TouchableOpacity onPress={() => this._navToQtyAdjustmentDetailView(item)}>
        <HStack
          w='full'
          p='3'
          space='2'
          bg={isOdd ? 'muted.50' : 'white'}
          borderBottomWidth='1'
          borderBottomColor='muted.200'
          alignItems='center'
        >
          <VStack flex={1} space='1'>
            <HStack w='full' alignItems='center' space='2'>
              <HStack px='2' py='0.5' borderRadius='md' bg={modeColor} alignItems='center' justifyContent='center'>
                <XText color='white' fontSize='xs' bold>
                  {modeText}
                </XText>
              </HStack>
              <XText variant='inactive' fontSize='xs'>
                {`#${id}`}
              </XText>
            </HStack>

            {ref_code && ref_code.length > 0 ? (
              <HStack w='full' space='1'>
                <XText variant='inactive' fontSize='sm'>
                  รหัสอ้างอิง:
                </XText>
                <XText fontSize='sm' numberOfLines={1}>
                  {ref_code}
                </XText>
              </HStack>
            ) : null}

            {note && note.length > 0 ? (
              <HStack w='full' space='1'>
                <XText variant='inactive' fontSize='sm'>
                  หมายเหตุ:
                </XText>
                <XText fontSize='sm' numberOfLines={1} flex={1}>
                  {note}
                </XText>
              </HStack>
            ) : null}

            <HStack w='full' alignItems='center' space='1'>
              <XText variant='inactive' fontSize='sm'>
                ดำเนินการเมื่อ:
              </XText>
              {/* <XIcon name='time-outline' family='Ionicons' size='xs' color='muted.400' /> */}
              <XText variant='inactive' fontSize='xs'>
                {createdAtText}
              </XText>
            </HStack>
          </VStack>

          <XIcon name='chevron-forward' family='Ionicons' color='muted.400' />
        </HStack>
      </TouchableOpacity>
    )
  }

  _renderListFooter = () => {
    const { isFetchingMore, isReachEnd, historyItems } = this.state

    if (isFetchingMore) {
      return (
        <HStack w='full' p='4' justifyContent='center'>
          <XSpinner />
        </HStack>
      )
    }

    if (isReachEnd && historyItems.length > 0) {
      return (
        <HStack w='full' p='4' justifyContent='center'>
          <XText variant='inactive'>ไม่มีรายการเพิ่มเติม</XText>
        </HStack>
      )
    }

    return null
  }

  _renderEmptyList = () => {
    const { isRefreshing } = this.state

    if (isRefreshing) {
      return null
    }

    return (
      <VStack w='full' h='200px' alignItems='center' justifyContent='center'>
        <XIcon name='file-tray-outline' family='Ionicons' size='2xl' color='muted.300' />
        <XText variant='inactive' mt='2'>
          ไม่พบรายการ
        </XText>
      </VStack>
    )
  }

  _renderTotalCount = () => {
    const { totalCount } = this.state

    return (
      <HStack w='full' px='3' py='2' bg='muted.100' borderBottomWidth='1' borderBottomColor='muted.200'>
        <XText variant='inactive' fontSize='sm'>
          {`ทั้งหมด ${totalCount} รายการ`}
        </XText>
      </HStack>
    )
  }

  _renderList = () => {
    const { isRefreshing, historyItems } = this.state

    if (isRefreshing) {
      return (
        <VStack flex={1} p='4' alignItems='center'>
          <XSpinner />
        </VStack>
      )
    }

    return (
      <FlatList
        data={historyItems}
        keyExtractor={(item, index) => `history_${item.id}_${index}`}
        renderItem={this._renderListItem}
        onEndReached={this._onEndReached}
        onEndReachedThreshold={0.3}
        ListFooterComponent={this._renderListFooter}
        ListEmptyComponent={this._renderEmptyList}
        refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={this._onRefresh} />}
      />
    )
  }

  render() {
    const { isInitialized } = this.state

    if (!isInitialized) {
      return (
        <XContainer>
          {this._renderCustomHeader()}
          <VStack flex={1} alignItems='center' justifyContent='center'>
            <XSpinner />
          </VStack>
        </XContainer>
      )
    }

    return (
      <XContainer>
        {this._renderCustomHeader()}
        {this._renderSearchBar()}
        {this._renderTotalCount()}
        {this._renderList()}
      </XContainer>
    )
  }
}

export default connect(
  (state) => ({
    selectedStore: getSelectedStore(state),
  }),
  (dispatch) => ({ dispatch })
)(QtyAdjustmentHistoryListView)
